import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CursorCandles } from "@/components/CursorCandles";
import { Crosshair } from "@/components/Crosshair";
import { CountUp, Reveal } from "@/components/Reveal";
import { ScoreLadder } from "@/components/ScoreLadder";
import { TickerStrip } from "@/components/TickerStrip";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";



import { ProfileCard } from "@/components/rank/ProfileCard";
import { ErrorPanel, LoadingPanel } from "@/components/rank/States";
import { fetchPreviewProfile, fetchSiteStats } from "@/lib/api";
import type { RankProfile, SiteStats } from "@/lib/types";





export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Hoodrank — Rank Every X Account by Who Follows It" },
      {
        name: "description",
        content:
          "Hoodrank scores any X handle by the quality of its followers — KOLs, VCs, founders, funds, whales. On-chain reputation for the Robinhood chain ecosystem.",
      },
      { property: "og:title", content: "Hoodrank — Reputation scoring for X" },
      {
        property: "og:description",
        content:
          "Score any Twitter handle by who actually follows it. On-chain reputation for the Robinhood ecosystem. No sign-up, no fluff.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Landing,
});







const STEPS = [
  {
    n: "01",
    title: "Paste a handle",
    body: "Drop any @username. No login, no wallet, no KYC. Works for your own account, your competitors, or the guy who just went viral.",
  },
  {
    n: "02",
    title: "See the graph",
    body: "Hoodrank pulls the full follower map — KOLs, VCs, founders, funds, whales. Every follower is weighted by their own on-chain footprint.",
  },
  {
    n: "03",
    title: "Get the score",
    body: "One number and the exact list of key followers driving your rank.",
  },
];

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="label mb-6">{children}</p>;
}

/** Landing preview — same card the app uses (@binance via API). */
function PreviewCard() {
  const [profile, setProfile] = useState<RankProfile | null>(null);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setProfile(null);
    setError("");
    fetchPreviewProfile()
      .then((p) => alive && setProfile(p))
      .catch((e: unknown) =>
        alive ? setError(e instanceof Error ? e.message : "Unknown error") : null,
      );
    return () => {
      alive = false;
    };
  }, [nonce]);

  if (error)
    return <ErrorPanel message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!profile) return <LoadingPanel label="Loading sample profile" />;
  return <ProfileCard profile={profile} />;
}

function useSiteStats() {
  const [stats, setStats] = useState<SiteStats | null>(null);
  useEffect(() => {
    let alive = true;
    fetchSiteStats().then((s) => alive && setStats(s));
    return () => {
      alive = false;
    };
  }, []);
  return (
    stats ?? {
      handlesRanked: 600,
      keyFollowers: 500_000,
      uptimePct: 99.99,
      lastBlock: 8_412_096,
    }
  );
}

function Landing() {
  const stats = useSiteStats();
  const receipts = [
    {
      value: stats.handlesRanked,
      decimals: 0,
      suffix: "+",
      label: "Handles ranked",
    },
    {
      value: stats.keyFollowers,
      decimals: 0,
      suffix: "+",
      label: "Key followers indexed",
    },
    {
      value: stats.uptimePct,
      decimals: 2,
      suffix: "%",
      label: "Uptime",
    },
  ];

  return (
    <>
      <CursorCandles />
      <Crosshair />
      <ScoreLadder />
      <SiteHeader />

      <main id="top">
        {/* Hero */}
        <section data-candles className="grid-paper border-b border-line">
          <div className="shell py-14 md:py-24">
            <Reveal>
              <span className="mono mb-7 inline-flex items-center gap-2 bg-accent px-3 py-1.5 text-[10px] font-semibold tracking-[0.18em] text-accent-ink uppercase sm:text-[11px]">
                <span className="size-1.5 bg-accent-ink" aria-hidden="true" />
                Live on Robinhood chain
              </span>
            </Reveal>

            <Reveal delay={90}>
              <h1 className="max-w-[24ch]">
                <span className="block">
                  RANK <span className="text-accent-deep">EVERY</span>
                </span>
                <span className="block">
                  <span className="hollow">ACCOUNT</span> ON X.
                </span>
              </h1>
            </Reveal>

            <Reveal delay={180}>
              <p className="mt-7 max-w-[52ch] text-[16px] text-ink-soft sm:text-[17px] md:mt-8">
                Score any Twitter handle by who actually follows it — KOLs,
                whales, founders, funds. On-chain reputation for the Robinhood
                ecosystem. No sign-up, no fluff. Paste a handle, get the rank.
              </p>
            </Reveal>

            <Reveal delay={260} className="mt-9 flex flex-wrap gap-3 md:mt-10">
              <Link to="/app" className="btn btn-dark w-full sm:w-auto">
                Check rank →
              </Link>
              <Link to="/docs" className="btn btn-outline w-full sm:w-auto">
                Read docs
              </Link>

            </Reveal>
          </div>

          {/* Ticker strip */}
          <TickerStrip />
        </section>




        {/* Section 2 — Product preview */}
        <section id="preview" className="border-b border-line">
          <div className="shell py-16 md:py-24">
            <Reveal>
              <Eyebrow>Read the room</Eyebrow>
              <h2 className="max-w-[18ch]">Who follows you is who you are.</h2>
              <p className="mt-6 max-w-[58ch] text-[16px] text-ink-soft">
                Every handle on Hoodrank gets scored by the quality of its
                followers, not the count. Bots don&apos;t move the needle. Signal
                does.
              </p>
            </Reveal>

            <Reveal delay={120} className="mt-12">
              <PreviewCard />
            </Reveal>
          </div>
        </section>


        {/* Section 3 — How it works */}
        <section id="how" className="bg-dark text-bg">
          <div className="shell py-16 md:py-24">
            <Reveal>
              <p className="label mb-6 !text-accent">Protocol</p>
              <h2 className="text-bg">Three moves in</h2>
              <p className="mt-6 max-w-[58ch] text-[16px] text-bg/70">
                Fully on-chain reputation. Scores, weights, and follower graph all
                verifiable — indexed live on Robinhood chain.
              </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3">
              {STEPS.map((s, i) => (
                <Reveal
                  key={s.n}
                  delay={i * 110}
                  className={
                    "border-t border-bg/20 py-8 md:border-t-0 md:px-8 " +
                    (i === 0 ? "md:pl-0" : "md:border-l md:border-bg/20")
                  }
                >
                  <p
                    className="font-display text-[clamp(84px,18vw,120px)] leading-[0.8] text-accent opacity-90"
                    aria-hidden="true"
                  >
                    {s.n}
                  </p>
                  <p className="label mt-6 !text-bg/50">Step {s.n}</p>
                  <h3 className="mt-3 text-bg">{s.title}</h3>
                  <p className="mt-4 text-[15px] text-bg/70">{s.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>


        {/* Section 4 — Receipts */}
        <section className="border-b border-line">
          <div className="shell py-16 md:py-24">
            <Reveal>
              <Eyebrow>On mainnet</Eyebrow>
              <h2>The receipts</h2>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 border-t border-line md:grid-cols-3">
              {receipts.map((r, i) => (
                <Reveal
                  key={r.label}
                  delay={i * 90}
                  className={
                    "border-b border-line py-8 md:border-b-0 md:px-8 md:py-10 " +
                    (i === 0 ? "md:pl-0" : "md:border-l md:border-line")
                  }
                >
                  <p className="font-display text-[clamp(48px,11vw,72px)] leading-[0.85] text-ink">
                    <CountUp
                      value={r.value}
                      decimals={r.decimals}
                      className="num font-display"
                    />

                    <span className="text-[0.52em] text-accent-deep">
                      {r.suffix}
                    </span>
                  </p>
                  <p className="label mt-4">{r.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5 — CTA */}
        <section data-candles id="cta" className="bg-dark text-bg">
          <div className="shell py-20 md:py-28">
            <Reveal>
              <h2 className="max-w-[20ch] text-bg">
                Don&apos;t chase followers.
                <br />
                Earn the rank.
              </h2>
              <p className="mono mt-6 text-[13px] tracking-[0.08em] text-accent uppercase">
                Hoodrank is live. Signal doesn&apos;t sleep.
              </p>
            </Reveal>
            <Reveal delay={120} className="mt-10 flex flex-wrap gap-3">
              <Link to="/app" className="btn btn-fill w-full sm:w-auto">
                Launch Hoodrank →
              </Link>
              <Link to="/docs" className="btn btn-outline-inv w-full sm:w-auto">
                Developer docs
              </Link>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter lastBlock={stats.lastBlock} />
    </>
  );
}

